import { useMemo, useState } from "react";
import useSWRMutation from "swr/mutation";
import { mutate } from "swr";
import { PlusIcon } from "lucide-react";
import { optimisticPrds, postFetcher, putFetcher, swrKey, writeAtom } from "@/lib/swr";
import { useTags, type TagItem } from "@/hooks/tags";
import { cn } from "@/lib/utils";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { TagPill, tagDotColor } from "./tag-pill";
import { CreateEntryButton } from "@/components/create-entry-button";
import type { Prd } from "@/types/domain";

// Tags on a PRD, shown as Linear-style label pills (colored dot + text). Tags are a
// global many-to-many vocabulary (tag table); the PRD stores associations, persisted
// as tag id arrays. The add entry is a Linear-style combobox: type to filter the
// global vocabulary (useTags), pick an existing one, or create a new label inline.
export const PrdTagsEditor = ({ prd }: { prd: Prd }) => {
  const allTags = useTags();
  // The PRD carries tag ids only; names/colors come from the vocabulary.
  const tags: TagItem[] = useMemo(() => {
    const byId = new Map(allTags.map((t) => [t.id, t]));
    return (prd.tagIds ?? [])
      .map((id) => byId.get(id))
      .filter((t): t is TagItem => !!t);
  }, [allTags, prd.tagIds]);
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const { trigger, isMutating } = useSWRMutation(`/prds/${prd.id}`, putFetcher<Prd>);
  const { trigger: createTag, isMutating: isCreating } = useSWRMutation(
    "/tags",
    postFetcher<TagItem>
  );

  const save = async (next: TagItem[]) => {
    const tagIds = next.map((t) => t.id);
    // The PUT echoes the full PRD → detail; list rows only need the ids.
    const saved = await trigger({ tagIds });
    if (saved) writeAtom(`/prds/${prd.id}`, saved);
    optimisticPrds(prd.id, { tagIds });
  };

  const addExisting = (tag: TagItem) => {
    setSearch("");
    if (tags.some((t) => t.id === tag.id)) return;
    save([...tags, tag]);
  };

  const create = async (rawName: string) => {
    const name = rawName.trim();
    setSearch("");
    if (!name) return;
    const created: TagItem = await createTag({ name });
    if (!created?.id || tags.some((t) => t.id === created.id)) return;
    await save([...tags, created]);
    mutate(swrKey("/tags")); // refresh the global vocabulary so the new label shows up
  };

  const remove = (tag: TagItem) => save(tags.filter((t) => t.id !== tag.id));

  const busy = isMutating || isCreating;
  const q = search.trim().toLowerCase();
  const suggestions = useMemo(() => {
    const taken = new Set(tags.map((t) => t.id));
    return allTags
      .filter((t) => !taken.has(t.id))
      .filter((t) => !q || t.name.toLowerCase().includes(q));
  }, [allTags, tags, q]);

  const canCreate =
    q.length > 0 &&
    !tags.some((t) => t.name.toLowerCase() === q) &&
    !allTags.some((t) => t.name.toLowerCase() === q);

  return (
    <div className="flex flex-wrap items-center gap-1.5">
      {tags.map((t) => (
        <TagPill
          key={t.id}
          tag={t.name}
          color={t.color}
          to={`/tags/${t.id}`}
          onRemove={() => remove(t)}
        />
      ))}

      <Popover
        open={open}
        onOpenChange={(o) => {
          setOpen(o);
          if (!o) setSearch("");
        }}
      >
        <PopoverTrigger asChild>
          {/* Always spelled out (owner 2026-09-14), with or without labels. */}
          <CreateEntryButton
            label="Add label"
            variant="ghost"
            size="xs"
            className="text-muted-foreground hover:text-foreground"
          />
        </PopoverTrigger>
        <PopoverContent align="start" className="w-56 p-0">
          <Command shouldFilter={false}>
            <CommandInput
              placeholder="Filter or create label…"
              value={search}
              onValueChange={setSearch}
            />
            <CommandList>
              {suggestions.length === 0 && !canCreate && (
                <CommandEmpty>No results.</CommandEmpty>
              )}
              {suggestions.length > 0 && (
                <CommandGroup heading="Labels">
                  {suggestions.map((t) => (
                    <CommandItem
                      key={t.id}
                      value={t.name}
                      disabled={busy}
                      onSelect={() => addExisting(t)}
                    >
                      <span
                        className={cn(
                          "mr-2 h-2 w-2 shrink-0 rounded-full",
                          t.color ?? tagDotColor(t.name)
                        )}
                      />
                      <span className="flex-1 truncate">{t.name}</span>
                      {t.count != null && (
                        <span className="ml-2 text-xs text-muted-foreground">
                          {t.count}
                        </span>
                      )}
                    </CommandItem>
                  ))}
                </CommandGroup>
              )}
              {canCreate && (
                <CommandGroup>
                  <CommandItem
                    value={`__create__${search}`}
                    disabled={busy}
                    onSelect={() => create(search)}
                  >
                    <PlusIcon className="mr-2 h-3.5 w-3.5 shrink-0" />
                    Create “{search.trim()}”
                  </CommandItem>
                </CommandGroup>
              )}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
};
